// Common types used across components
import { v4 as uuidv4 } from 'uuid';
export interface NavLink {
  text: string;
  href: string;
}

export interface ProductData {
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  imageSrc: string;
  imageAlt: string;
  reverse: boolean;
  backgroundColor: 'white' | 'pink';
}

// Collection and Product types for admin
export type ProductCollectionType = 
  | 'Everyday Cakes' 
  | 'Cake & Cupcake Set' 
  | 'Celebration Cakes' 
  | 'Custom Cake Flavours' 
  | 'Create Your Own Cake'
  | 'Wedding Cakes'
  | 'Desserts';

export interface ProductCollection {
  id: string;
  name: ProductCollectionType;
  description: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductImage {
  id: string;
  url: string;
  alt: string;
  isPrimary: boolean;
  file?: File; // Optional File object for handling uploads
}

export interface ProductFlavor {
  name: string;
  available: boolean;
  price?: number; // Optional price for the flavor (additional cost)
}

export interface ProductSize {
  name: string;
  servingSize: number;
  price: number;
  // Optional tiered quantities (used mainly for Desserts)
  // Each tier represents a bundle quantity and its total base price for this size
  quantityTiers?: {
    quantity: number;
    price: number;
  }[];
}

export interface Product {
  id: string;
  name: string;
  description: string;
  collection: ProductCollectionType;
  collectionId: string;
  images: ProductImage[];
  flavors: ProductFlavor[];
  sizes: ProductSize[];
  availableShapes: Array<'Round' | 'Heart' | 'Square' | 'Star'>;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  // New fields based on Excel template
  availableLayers?: string[]; // "How many layers available"
  maxFlavorsSelectable?: number; // "How many flavors can be selected"
  fillings?: string[]; // "Filling"
  coverIcingOptions?: string[]; // "Cover icing offered"
  addOns?: string[]; // "Add-ons" with names and costs
  matchingCupcakes?: string[]; // "Matching Cupcakes" with names and costs (single select in UI)
  // Frontend behavior flags
  enableBaseColor?: boolean; // Whether base color selection is shown & required
  enablePipingColor?: boolean; // Whether piping color selection is shown & required
  enableMessageOnCake?: boolean; // Whether message-on-cake textarea is shown
  enableImageOnCake?: boolean; // Whether image-on-cake upload/checkbox is shown
  enableSampleImage?: boolean; // Whether sample image upload section is shown
}

// Button component types
export interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'purple';
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

// Icon component types
export interface IconProps {
  type: 'search' | 'user' | 'cart' | 'heart' | 'arrow' | 'arrow-left' | 'arrow-right' | 'dashboard' | 'cake' | 'users' | 'chart' | 'gear' | 'clock' | 'dollar' | 'logout' | 'menu' | 'notification' | 'edit' | 'delete' | 'add' | 'upload' | 'image' | 'download' | 'error' | 'calendar' | 'storefront' | 'copy' | 'refresh';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  onClick?: () => void;
}

// Admin navigation types
export interface NavItemData {
  id: string;
  label: string;
  icon: 'dashboard' | 'cart' | 'cake' | 'calendar' | 'users' | 'chart' | 'gear' | 'storefront';
}

// Input component types
export interface InputProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'textarea' | 'select' | 'file';
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  value?: string | number;
  options?: Array<{value: string, label: string}>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  accept?: string;
  multiple?: boolean;
  error?: string;
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
}

// Navigation component types
export interface NavigationProps {
  links: NavLink[];
  onLinkClick?: () => void;
  isMobile?: boolean;
}

// PromotionalBanner component types
export interface PromotionalBannerProps {
  message: string;
  code: string;
  dates?: string;
  note?: string;
  onClick?: () => void;
}

// EmailSubscription component types
export interface EmailSubscriptionProps {
  onSubmit?: (email: string) => void;
}

// Header component types
export interface HeaderProps {
  navLinks: NavLink[];
}

// HeroSection component types
export interface HeroSectionProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string;
  imageSrc?: string;
}

// ProductCard component types
export interface ProductCardProps {
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  imageSrc: string;
  imageAlt: string;
  reverse?: boolean;
  backgroundColor?: 'white' | 'pink';
}

// Footer component types
export interface FooterProps {
  onEmailSubmit?: (email: string) => void;
  onFollowShop?: () => void;
}

// Admin Product Management Types
export interface TableRowProps {
  children: React.ReactNode;
  onClick?: () => void;
  isHeader?: boolean;
  className?: string;
}

export interface TableHeaderProps {
  headers: string[];
  className?: string;
}

export interface UploadButtonProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  multiple?: boolean;
  accept?: string;
  className?: string;
  children: React.ReactNode;
}

export interface BadgeProps {
  text: string;
  variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  className?: string;
}

export interface ProductTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onDuplicate: (product: Product) => void;
  loading?: boolean;
}

export interface ProductFormProps {
  product?: Product;
  collections: ProductCollection[];
  onSubmit: (product: Partial<Product>) => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface ImageUploaderProps {
  images: ProductImage[];
  onImagesChange: (images: ProductImage[]) => void;
  className?: string;
}

export interface ProductsManagementProps {
  products: Product[];
  collections: ProductCollection[];
  loading?: boolean;
  onCreateProduct: (product: Partial<Product>) => void;
  onUpdateProduct: (product: Product) => void;
  onDeleteProduct: (product: Product) => void;
  onDuplicateProduct: (product: Product) => void;
  onRefresh?: () => void;
  selectedCollectionId?: string;
  onCollectionFilterChange?: (collectionId: string) => void;
}

// Staff/Admin User Types
export type StaffRole = 'admin' | 'manager' | 'editor' | 'viewer';

export interface StaffPermissions {
  canManageProducts: boolean;
  canManageOrders: boolean;
  canManageCustomers: boolean;
  canManageStaff: boolean;
  canViewAnalytics: boolean;
  canManageSettings: boolean;
}

export interface StaffNotificationSettings {
  emailNotifications: boolean;
  orderUpdates: boolean;
  productUpdates: boolean;
  customerUpdates: boolean;
  marketingUpdates: boolean;
}

export interface StaffUser {
  id: string;
  uid: string; // Firebase Auth UID
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  role: StaffRole;
  permissions: StaffPermissions;
  notificationSettings: StaffNotificationSettings;
  phoneNumber?: string;
  photoURL?: string;
  isActive: boolean;
  lastLogin: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Settings Page Component Types
export interface FormFieldProps {
  id: string;
  label: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'select' | 'textarea' | 'number' | 'date';
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  options?: Array<{value: string, label: string}>;
  error?: string;
  disabled?: boolean;
  className?: string;
  min?: number;
  step?: number;
}

export interface ProfileAvatarProps {
  photoURL?: string;
  displayName: string;
  size?: 'small' | 'medium' | 'large';
  onUpload?: (file: File) => void;
  className?: string;
}

export interface ToggleProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export interface ProfileFormProps {
  user: StaffUser;
  onUpdate: (userData: Partial<StaffUser>) => void;
  loading?: boolean;
}

export interface SecuritySettingsProps {
  user: StaffUser;
  onUpdate: (userData: Partial<StaffUser>) => void;
  onPasswordReset: () => void;
  loading?: boolean;
}

export interface NotificationSettingsProps {
  settings: StaffNotificationSettings;
  onUpdate: (settings: StaffNotificationSettings) => void;
  loading?: boolean;
}

export interface SettingsManagementProps {
  user: StaffUser;
  loading?: boolean;
  onUpdateProfile: (userData: Partial<StaffUser>) => void;
  onUpdateSecurity: (userData: Partial<StaffUser>) => void;
  onPasswordReset: () => void;
  onUpdateNotifications: (settings: StaffNotificationSettings) => void;
  // Delivery pricing props
  deliveryTiers?: DeliveryTier[];
  deliverySettings?: DeliveryPricingSettings | null;
  onCreateDeliveryTier?: (tier: Partial<DeliveryTier>) => void;
  onUpdateDeliveryTier?: (tierId: string, updates: Partial<DeliveryTier>) => void;
  onDeleteDeliveryTier?: (tierId: string) => void;
  onUpdateDeliverySettings?: (settings: Partial<DeliveryPricingSettings>) => void;
  // VAT settings props
  vatSettings?: VatSettings | null;
  onUpdateVatSettings?: (settings: Partial<VatSettings>) => void;
  // Image on cake settings props
  imageOnCakeSettings?: ImageOnCakeSettings | null;
  onUpdateImageOnCakeSettings?: (settings: Partial<ImageOnCakeSettings>) => void;
}

// ============================================================================
// CART TYPES - Shopping Cart Functionality
// ============================================================================

// Cart Item represents a single product with all its customizations in the cart
export interface CartItem {
  id: string; // Unique cart item ID (UUID)
  productId: string; // Reference to the Product
  name: string; // Product name for display
  imageUrl: string; // Primary product image URL
  
  // Product customizations
  size: string; // Selected size name
  flavors: string[]; // Selected flavor names
  shape: string; // Selected shape
  layers: string; // Selected layers
  filling: string; // Selected filling
  icing: string; // Selected cover icing
  baseColor: string; // Selected base color
  pipingColor: string; // Selected piping color
  specialInstructions: string; // Custom instructions
  addOns: string[]; // Selected add-ons
  matchingCupcakes: string[]; // Selected matching cupcakes
  hasImageOnCake: boolean; // Whether image should be printed on cake
  cakeImageUrl?: string; // URL of the uploaded cake image
  hasSampleImage: boolean; // Whether a sample image was provided
  sampleImageUrl?: string; // URL of the uploaded sample image
  
  // Delivery information
  deliveryOption: 'collection' | 'delivery'; // Collection or delivery option
  deliveryDate?: Date; // Selected delivery date (only for delivery option)
  timeWindowStart?: string; // Selected window start, e.g. "12:00 PM"
  timeWindowEnd?: string; // Selected window end, e.g. "1:00 PM"
  
  // Pricing information
  quantity: number; // Number of this item in cart
  basePrice: number; // Base price from selected size
  flavorAdditionalPrice: number; // Additional cost for selected flavors
  fillingAdditionalPrice: number; // Additional cost for selected filling
  icingAdditionalPrice: number; // Additional cost for selected icing
  addOnsAdditionalPrice: number; // Additional cost for selected add-ons
  matchingCupcakesAdditionalPrice: number; // Additional cost for selected matching cupcakes
  imageOnCakeAdditionalPrice: number; // Additional cost for image on cake
  baseColorAdditionalPrice: number; // Additional cost for selected base color (not piping)
  unitPrice: number; // Total price per unit (base + additions)
  totalPrice: number; // Total price for this line item (unitPrice * quantity)
  
  // Metadata
  addedAt: Date; // When this item was added to cart
  updatedAt: Date; // When this item was last modified
}

// Cart represents the entire shopping cart state
export interface Cart {
  items: CartItem[]; // Array of cart items
  totalItems: number; // Total quantity of all items
  subtotal: number; // Sum of all item total prices (excluding VAT)
  vat: number; // Calculated VAT amount
  vatPercentage: number; // VAT percentage applied
  isInclusive: boolean; // Whether prices include VAT
  shipping: number; // Shipping cost
  coupon?: Coupon; // Applied coupon
  couponDiscount: number; // Discount amount from coupon
  total: number; // Grand total (subtotal + vat + shipping - couponDiscount)
  lastUpdated: Date; // When cart was last modified
  version: string; // Cart data version for migration
}

// Cart operations for the context
export interface CartOperations {
  addItem: (item: CartItemCreateData) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  getCart: () => Cart;
  getItemCount: () => number;
  getTotalPrice: () => number;
  updateCartWithVat: () => Promise<void>;
  applyCoupon: (couponCode: string) => Promise<boolean>;
  removeCoupon: () => Promise<void>;
  clearVatCache: () => void;
}

// Cart context state
export interface CartContextType {
  cart: Cart;
  operations: CartOperations;
  loading: boolean;
  error: string | null;
}

// Cart service interface for localStorage operations
export interface CartService {
  saveCart: (cart: Cart) => void;
  loadCart: () => Cart | null;
  clearCart: () => void;
  validateCart: (cart: Cart) => boolean;
  migrateCart: (oldCart: any) => Cart;
  updateCartShipping: (cart: Cart, customerInfo: { deliveryOption?: 'collection' | 'delivery'; address?: { postalCode?: string } }) => Promise<Cart>;
}

// Cart validation result
export interface CartValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Cart item creation data (from ProductDetailPage)
export interface CartItemCreateData {
  productId: string;
  name: string;
  imageUrl: string;
  size: string;
  flavors: string[];
  shape: string;
  layers: string;
  filling: string;
  icing: string;
  baseColor: string;
  pipingColor: string;
  specialInstructions: string;
  addOns: string[];
  matchingCupcakes: string[];
  hasImageOnCake: boolean;
  cakeImageUrl?: string;
  hasSampleImage: boolean;
  sampleImageUrl?: string;
  deliveryOption: 'collection' | 'delivery';
  deliveryDate?: Date;
  timeWindowStart?: string;
  timeWindowEnd?: string;
  quantity: number;
  basePrice: number;
  quantityTierTotalPrice?: number; // For desserts: total price from quantity tier (already includes quantity)
  flavorAdditionalPrice: number;
  fillingAdditionalPrice: number;
  icingAdditionalPrice: number;
  addOnsAdditionalPrice: number;
  matchingCupcakesAdditionalPrice: number;
  imageOnCakeAdditionalPrice: number;
  baseColorAdditionalPrice: number;
}

// Cart component props
export interface CartIconProps {
  itemCount: number;
  onClick: () => void;
  className?: string;
}

export interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

export interface CartItemProps {
  item: CartItem;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemove: (itemId: string) => void;
  className?: string;
}

export interface CartPageProps {
  className?: string;
}

// Cart notification types
export type CartNotificationType = 'success' | 'error' | 'warning' | 'info';

export interface CartNotification {
  id: string;
  type: CartNotificationType;
  title: string;
  message: string;
  duration?: number; // Auto-dismiss duration in ms
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Cart analytics events
export interface CartAnalyticsEvent {
  event: 'add_to_cart' | 'remove_from_cart' | 'update_quantity' | 'view_cart' | 'clear_cart';
  itemId?: string;
  productId?: string;
  quantity?: number;
  value?: number;
  timestamp: Date;
  metadata?: Record<string, any>;
}

// ============================================================================
// PAYMENT & ORDER TYPES - Stripe Integration
// ============================================================================

// Stripe Configuration
export interface StripeConfig {
  id: string;
  publishableKey: string;
  secretKey: string; // Encrypted
  webhookSecret?: string; // Encrypted
  environment: 'test' | 'live';
  isActive: boolean;
  lastUpdated: Date;
  updatedBy: string;
  createdAt: Date;
}

// Customer Information
export interface CustomerInfo {
  id?: string; // Stripe customer ID
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string;
  };
  deliveryInstructions?: string;
  deliveryOption?: 'collection' | 'delivery';
}

// Payment Information
export interface PaymentInfo {
  id: string; // Stripe payment intent ID
  status: PaymentStatus;
  amount: number; // Amount in cents
  currency: string;
  method: PaymentMethod;
  last4?: string; // Last 4 digits of card
  brand?: string; // Card brand (visa, mastercard, etc.)
  expMonth?: number;
  expYear?: number;
  createdAt: Date;
  updatedAt: Date;
}

// Payment Status
export type PaymentStatus = 
  | 'pending'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'canceled'
  | 'requires_action'
  | 'requires_confirmation'
  | 'requires_payment_method';

// Payment Method
export type PaymentMethod = 'card' | 'bank_transfer' | 'cash' | 'other';

// Order Status
export type OrderStatus = 
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'ready_for_collection'
  | 'out_for_delivery'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'refunded';

// Order Item (extends CartItem with order-specific data)
export interface OrderItem extends CartItem {
  orderId: string;
  status: OrderStatus;
  notes?: string; // Admin notes
  estimatedCompletion?: Date;
  actualCompletion?: Date;
  // Explicitly include image URLs to ensure they're preserved
  hasImageOnCake: boolean;
  cakeImageUrl?: string;
  hasSampleImage: boolean;
  sampleImageUrl?: string;
}

// Order
export interface Order {
  id: string;
  orderNumber: string; // Human-readable order number (e.g., TFR-2024-001)
  customer: CustomerInfo;
  items: OrderItem[];
  payment: PaymentInfo;
  status: OrderStatus;
  
  // Pricing
  subtotal: number;
  vat: number;
  vatPercentage: number;
  isInclusive: boolean;
  shipping: number;
  coupon?: Coupon; // Applied coupon
  couponDiscount: number; // Discount amount from coupon
  originalTotal: number; // Total before coupon discount
  total: number; // Final total after coupon discount
  
  // Delivery Information
  deliveryOption: 'collection' | 'delivery';
  deliveryDate?: Date;
  deliveryAddress?: CustomerInfo['address'];
  deliveryInstructions?: string;
  
  // Collection/Delivery Times (12-hour format with AM/PM, e.g., "9:00 AM", "5:00 PM")
  collectionStartTime?: string;
  collectionEndTime?: string;
  deliveryStartTime?: string;
  deliveryEndTime?: string;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  confirmedAt?: Date;
  completedAt?: Date;
  
  // Admin
  assignedTo?: string; // Admin user ID
  notes?: string; // Admin notes
  tags?: string[]; // For categorization
  
  // Metadata
  source: 'website' | 'admin' | 'api';
  version: string; // Order data version for migration
}

// Order Creation Data
export interface OrderCreateData {
  customer: CustomerInfo;
  items: CartItem[];
  deliveryOption: 'collection' | 'delivery';
  deliveryDate?: Date;
  deliveryAddress?: CustomerInfo['address'];
  deliveryInstructions?: string;
  paymentMethodId: string; // Stripe payment method ID
  coupon?: Coupon; // Applied coupon
  couponDiscount?: number; // Discount amount from coupon
}

// Manual order line item (for admin-created orders from phone, in-person, etc.)
// Customization fields match CartItem so manual orders display like website orders in OrderDetailModal.
export interface ManualOrderLineItem {
  productId?: string;
  name: string;
  quantity: number;
  unitPrice: number; // Price per unit in euros
  // Customization (same sections as website orders)
  size?: string;
  shape?: string;
  layers?: string;
  flavors?: string[]; // e.g. ["Vanilla", "Chocolate"] — admin can enter comma-separated
  filling?: string;
  icing?: string;
  baseColor?: string;
  pipingColor?: string;
  specialInstructions?: string; // Message on cake
  addOns?: string[];
  matchingCupcakes?: string[];
  description?: string; // Optional extra notes (legacy)
}

// Manual order creation data (admin-created orders, source: 'admin')
export interface ManualOrderCreateData {
  customer: CustomerInfo;
  items: ManualOrderLineItem[];
  deliveryOption: 'collection' | 'delivery';
  deliveryDate?: Date;
  deliveryAddress?: CustomerInfo['address'];
  deliveryInstructions?: string;
  collectionStartTime?: string;
  collectionEndTime?: string;
  deliveryStartTime?: string;
  deliveryEndTime?: string;
  paymentMethod: PaymentMethod;
  paymentId?: string; // Reference: Stripe pi_xxx, bank ref, "CASH", etc.
  shippingAmount?: number; // Manual shipping in euros (default from service if delivery)
  coupon?: Coupon;
  couponDiscount?: number;
  // Admin-only discount helpers: apply a fixed amount or a percentage to the total cost.
  // - fixed: amount in euros to subtract from (subtotal + VAT + shipping)
  // - percentage: percent (0-100) to subtract from (subtotal + VAT + shipping)
  adminDiscountType?: CouponDiscountType;
  adminDiscountValue?: number;
  adminNotes?: string;
}

// Order Update Data
export interface OrderUpdateData {
  status?: OrderStatus;
  assignedTo?: string;
  notes?: string;
  tags?: string[];
  estimatedCompletion?: Date;
  actualCompletion?: Date;
  // Collection/Delivery Times (12-hour format with AM/PM)
  collectionStartTime?: string;
  collectionEndTime?: string;
  deliveryStartTime?: string;
  deliveryEndTime?: string;
  // Order items and pricing (for edit functionality)
  items?: OrderItem[];
  subtotal?: number;
  vat?: number;
  total?: number;
  originalTotal?: number;
  deliveryDate?: Date;
  deliveryOption?: 'collection' | 'delivery';
}

// Order Item Summary (simplified item details for grid view)
export interface OrderItemSummary {
  name: string;
  quantity: number;
  size?: string;
  shape?: string;
  layers?: string;
  flavors?: string[];
  filling?: string;
  icing?: string;
  baseColor?: string;
  pipingColor?: string;
  addOns?: string[];
  matchingCupcakes?: string[];
  hasImageOnCake?: boolean;
  specialInstructions?: string;
  unitPrice: number;
  totalPrice: number;
}

// Order Summary (for lists)
export interface OrderSummary {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  status: OrderStatus;
  total: number;
  itemCount: number;
  itemNames?: string[]; // Names of items in the order
  itemDetails?: OrderItemSummary[]; // Detailed item information for display
  createdAt: Date;
  deliveryDate?: Date;
  deliveryOption?: 'collection' | 'delivery';
  paymentStatus: PaymentStatus;
  // Collection/delivery time windows (12-hour format, e.g. "9:00 AM", "5:00 PM") for calendar export
  collectionStartTime?: string;
  collectionEndTime?: string;
  deliveryStartTime?: string;
  deliveryEndTime?: string;
}

// Order Filter Options
export interface OrderFilters {
  status?: OrderStatus[];
  paymentStatus?: PaymentStatus[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  assignedTo?: string;
  tags?: string[];
  search?: string; // Search in order number, customer name, email
}

// Order Statistics
export interface OrderStats {
  totalOrders: number;
  pendingOrders: number;
  completedOrders: number;
  cancelledOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersByStatus: Record<OrderStatus, number>;
  ordersByPaymentStatus: Record<PaymentStatus, number>;
  revenueByMonth: Array<{
    month: string;
    revenue: number;
    orders: number;
  }>;
}

// Stripe Webhook Events
export interface StripeWebhookEvent {
  id: string;
  type: string;
  data: {
    object: any;
  };
  created: number;
  livemode: boolean;
}

// Payment Intent Result
export interface PaymentIntentResult {
  success: boolean;
  paymentIntentId?: string;
  clientSecret?: string;
  error?: string;
  requiresAction?: boolean;
}

// Refund Information
export interface RefundInfo {
  id: string; // Stripe refund ID
  amount: number; // Amount in cents
  reason: 'duplicate' | 'fraudulent' | 'requested_by_customer' | 'other';
  status: 'pending' | 'succeeded' | 'failed' | 'canceled';
  createdAt: Date;
  notes?: string;
}

// Order Service Interface
export interface OrderService {
  createOrder: (data: OrderCreateData) => Promise<Order>;
  createManualOrder: (data: ManualOrderCreateData) => Promise<Order>;
  getOrder: (orderId: string) => Promise<Order | null>;
  getOrders: (filters?: OrderFilters, limit?: number, offset?: number) => Promise<OrderSummary[]>;
  updateOrder: (orderId: string, data: OrderUpdateData) => Promise<Order>;
  cancelOrder: (orderId: string, reason?: string) => Promise<Order>;
  refundOrder: (orderId: string, amount?: number, reason?: string) => Promise<RefundInfo>;
  getOrderStats: (dateRange?: { start: Date; end: Date }) => Promise<OrderStats>;
  generateOrderNumber: () => Promise<string>;
  deleteOrdersByStatus: (status: OrderStatus) => Promise<number>;
  deleteOrder: (orderId: string) => Promise<boolean>;
}

// Stripe Service Interface
export interface StripeService {
  createPaymentIntent: (amount: number, currency: string, metadata?: Record<string, any>) => Promise<PaymentIntentResult>;
  confirmPayment: (paymentIntentId: string, paymentMethodId: string) => Promise<PaymentIntentResult>;
  createRefund: (paymentIntentId: string, amount?: number, reason?: string) => Promise<RefundInfo>;
  getPaymentIntent: (paymentIntentId: string) => Promise<PaymentInfo | null>;
  createCustomer: (customerInfo: CustomerInfo) => Promise<string>; // Returns Stripe customer ID
  findCustomerByEmail: (email: string) => Promise<string | null>; // Find existing customer by email
  createOrGetCustomer: (customerInfo: CustomerInfo) => Promise<string>; // Create or get existing customer
  updateCustomer: (customerId: string, customerInfo: Partial<CustomerInfo>) => Promise<void>;
}

// Stripe Configuration Service Interface
export interface StripeConfigService {
  getConfig: () => Promise<StripeConfig | null>;
  updateConfig: (config: Partial<StripeConfig>) => Promise<StripeConfig>;
  testConnection: (config: Partial<StripeConfig>) => Promise<boolean>;
  encryptSecret: (secret: string) => string;
  decryptSecret: (encryptedSecret: string) => string;
}

// Cart utility functions
export const createCartItemId = (): string => uuidv4();

export const createEmptyCart = (): Cart => ({
  items: [],
  totalItems: 0,
  subtotal: 0,
  vat: 0,
  vatPercentage: 0,
  isInclusive: false,
  shipping: 0,
  couponDiscount: 0,
  total: 0,
  lastUpdated: new Date(),
  version: '1.0.0'
});

export const calculateCartTotals = (items: CartItem[], couponDiscount: number = 0, vatSettings?: { vatPercentage: number; isInclusive: boolean }): Pick<Cart, 'totalItems' | 'subtotal' | 'vat' | 'vatPercentage' | 'isInclusive' | 'shipping' | 'couponDiscount' | 'total'> => {
  const totalItems = items.reduce((sum, item) => {
    return sum + (item?.quantity || 0);
  }, 0);
  
  const subtotal = items.reduce((sum, item) => {
    return sum + (item?.totalPrice || 0);
  }, 0);
  
  // Use admin-controlled VAT settings or default to disabled VAT
  const vatPercentage = vatSettings?.vatPercentage ?? 0; // Default to 0 if undefined
  const isInclusive = vatSettings?.isInclusive ?? false;
  
  // Calculate VAT based on whether pricing is inclusive or exclusive
  const vat = isInclusive 
    ? Math.round(subtotal * vatPercentage / (100 + vatPercentage) * 100) / 100  // VAT already included
    : Math.round(subtotal * vatPercentage / 100 * 100) / 100;  // VAT to be added
  
  // const shipping = subtotal > 50 ? 0 : 5; // Free shipping over €50
  const shipping = 0;
  const total = Math.max(0, subtotal + vat + shipping - couponDiscount);
  
  return {
    totalItems,
    subtotal: Math.round(subtotal * 100) / 100,
    vat,
    vatPercentage,
    isInclusive,
    shipping,
    couponDiscount,
    total: Math.round(total * 100) / 100
  };
};

// ============================================================================
// AVAILABILITY TYPES - Calendar and Booking Management
// ============================================================================

// Time slot for specific time-based availability
export interface TimeSlot {
  id: string;
  startTime: string; // Format: "09:00"
  endTime: string; // Format: "10:00"
  isAvailable: boolean;
  maxBookings?: number; // Optional limit on bookings for this time slot
  currentBookings: number; // Current number of bookings for this time slot
  label?: string; // Optional label like "Morning", "Afternoon", etc.
}

// Individual date availability record
export interface AvailabilityDate {
  id: string;
  date: Date; // The specific date
  isAvailable: boolean; // Whether bookings are accepted for this date
  maxBookings?: number; // Optional limit on total bookings per day
  currentBookings: number; // Current number of bookings for this date
  timeSlots?: TimeSlot[]; // Optional time-based availability
  notes?: string; // Admin notes about the date (holidays, special circumstances, etc.)
  isBlocked: boolean; // Whether this date is completely blocked (holidays, maintenance, etc.)
  createdAt: Date;
  updatedAt: Date;
}

// Global availability settings
export interface AvailabilitySettings {
  id: string;
  defaultMaxBookings: number; // Legacy fallback if weekday/weekend caps are unset
  weekdayMaxBookings: number; // Max bookings Mon–Fri
  weekendMaxBookings: number; // Max bookings Sat–Sun
  advanceBookingDays: number; // How many days in advance customers can book
  workingDays: number[]; // [1,2,3,4,5] for Mon-Fri (0=Sunday, 1=Monday, etc.)
  workingHours: {
    start: string; // Format: "09:00"
    end: string; // Format: "17:00"
  };
  blockedDates: Date[]; // Holidays, maintenance days, etc.
  timeSlotDuration: number; // Duration of each time slot in minutes (default: 60)
  enableTimeSlots: boolean; // Whether to use time-based booking
  createdAt: Date;
  updatedAt: Date;
}

// Availability status for display purposes
export type AvailabilityStatus = 'available' | 'limited' | 'full' | 'blocked' | 'past';

// Calendar view modes
export type CalendarViewMode = 'month' | 'week' | 'day';

// Availability management operations
export interface AvailabilityOperations {
  createAvailabilityDate: (date: Date, settings: Partial<AvailabilityDate>) => Promise<AvailabilityDate>;
  updateAvailabilityDate: (id: string, updates: Partial<AvailabilityDate>) => Promise<void>;
  deleteAvailabilityDate: (id: string) => Promise<void>;
  getAvailabilityDate: (date: Date) => Promise<AvailabilityDate | null>;
  getAvailabilityRange: (startDate: Date, endDate: Date) => Promise<AvailabilityDate[]>;
  bulkUpdateAvailability: (dates: Date[], updates: Partial<AvailabilityDate>) => Promise<void>;
  getAvailabilitySettings: () => Promise<AvailabilitySettings>;
  updateAvailabilitySettings: (settings: Partial<AvailabilitySettings>) => Promise<void>;
}

// Calendar component props
export interface CalendarProps {
  selectedDate?: Date;
  onDateSelect: (date: Date) => void;
  onDateClick?: (date: Date) => void;
  availabilityDates?: AvailabilityDate[];
  availabilitySettings?: AvailabilitySettings | null;
  viewMode?: CalendarViewMode;
  className?: string;
  disabled?: boolean;
  currentMonth?: Date;
  onMonthChange?: (date: Date) => void;
  /** When true, blocked/full dates remain clickable (for admin availability management) */
  adminMode?: boolean;
}

// Calendar cell props for individual date cells
export interface CalendarCellProps {
  date: Date;
  isSelected: boolean;
  isToday: boolean;
  isCurrentMonth: boolean;
  availability?: AvailabilityDate;
  availabilitySettings?: AvailabilitySettings | null;
  isSelectable?: boolean;
  onClick: (date: Date) => void;
  className?: string;
  /** Admin calendars show the booking count; storefront shows color only */
  showBookingCount?: boolean;
}

// Availability page component props
export interface AvailabilityPageProps {
  className?: string;
}

// Availability modal props for editing specific dates
export interface AvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
  availability?: AvailabilityDate;
  onSave: (availability: AvailabilityDate) => void;
  loading?: boolean;
}

// Availability settings panel props
export interface AvailabilitySettingsProps {
  settings: AvailabilitySettings;
  onUpdate: (settings: Partial<AvailabilitySettings>) => void;
  loading?: boolean;
}

// Bulk availability update props
export interface BulkAvailabilityUpdateProps {
  selectedDates: Date[];
  onUpdate: (dates: Date[], updates: Partial<AvailabilityDate>) => void;
  onClearSelection: () => void;
  loading?: boolean;
}

// Availability context for state management
export interface AvailabilityContextType {
  availabilityDates: AvailabilityDate[];
  settings: AvailabilitySettings | null;
  loading: boolean;
  error: string | null;
  operations: AvailabilityOperations;
  selectedDates: Date[];
  setSelectedDates: (dates: Date[]) => void;
}

// Availability validation result
export interface AvailabilityValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Availability analytics for reporting
export interface AvailabilityAnalytics {
  totalAvailableDays: number;
  totalBookedDays: number;
  averageBookingsPerDay: number;
  mostPopularDays: Array<{
    date: Date;
    bookingCount: number;
  }>;
  leastPopularDays: Array<{
    date: Date;
    bookingCount: number;
  }>;
  timeSlotAnalytics?: Array<{
    timeSlot: string;
    bookingCount: number;
    utilizationRate: number;
  }>;
}

// Utility functions for availability
export const createAvailabilityDateId = (date: Date): string => {
  return getDateKey(date);
};

export const isWeekendDate = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

export const normalizeAvailabilitySettings = (
  settings: Partial<AvailabilitySettings> & Pick<AvailabilitySettings, 'id'>
): AvailabilitySettings => {
  const defaults = createDefaultAvailabilitySettings();
  const fallback = settings.defaultMaxBookings ?? defaults.defaultMaxBookings;
  const weekdayMaxBookings = settings.weekdayMaxBookings ?? fallback;
  const weekendMaxBookings = settings.weekendMaxBookings ?? fallback;

  return {
    ...defaults,
    ...settings,
    weekdayMaxBookings,
    weekendMaxBookings,
    defaultMaxBookings: settings.defaultMaxBookings ?? weekdayMaxBookings
  };
};

export const resolveMaxBookingsForDate = (
  date: Date,
  settings?: AvailabilitySettings | null,
  availability?: AvailabilityDate | null
): number | undefined => {
  if (availability?.maxBookings != null) {
    return availability.maxBookings;
  }

  if (!settings) {
    return undefined;
  }

  const normalized = normalizeAvailabilitySettings(settings);
  return isWeekendDate(date)
    ? normalized.weekendMaxBookings
    : normalized.weekdayMaxBookings;
};

export const isDateAvailable = (
  availability: AvailabilityDate,
  settings?: AvailabilitySettings | null
): boolean => {
  if (availability.isBlocked || !availability.isAvailable) {
    return false;
  }

  const maxBookings = resolveMaxBookingsForDate(availability.date, settings, availability);
  if (maxBookings != null && availability.currentBookings >= maxBookings) {
    return false;
  }

  return true;
};

export const getAvailabilityStatus = (
  availability: AvailabilityDate,
  currentDate: Date = new Date(),
  settings?: AvailabilitySettings | null
): AvailabilityStatus => {
  const today = new Date(currentDate);
  today.setHours(0, 0, 0, 0);
  const date = new Date(availability.date);
  date.setHours(0, 0, 0, 0);

  // Check if date is in the past
  if (date < today) {
    return 'past';
  }
  
  // Check if date is blocked
  if (availability.isBlocked || !availability.isAvailable) {
    return 'blocked';
  }

  const maxBookings = resolveMaxBookingsForDate(availability.date, settings, availability);
  if (maxBookings != null) {
    const remainingSlots = Math.max(0, maxBookings - availability.currentBookings);

    if (remainingSlots === 0) {
      return 'full';
    }

    if (remainingSlots < maxBookings) {
      return 'limited';
    }
  }
  
  return 'available';
};

export const createEmptyAvailabilityDate = (date: Date): AvailabilityDate => ({
  id: createAvailabilityDateId(date),
  date,
  isAvailable: true,
  currentBookings: 0,
  isBlocked: false,
  createdAt: new Date(),
  updatedAt: new Date()
});

export const createDefaultAvailabilitySettings = (): AvailabilitySettings => ({
  id: 'default',
  defaultMaxBookings: 10,
  weekdayMaxBookings: 10,
  weekendMaxBookings: 10,
  advanceBookingDays: 30,
  workingDays: [1, 2, 3, 4, 5], // Monday to Friday
  workingHours: {
    start: '09:00',
    end: '17:00'
  },
  blockedDates: [],
  timeSlotDuration: 60, // 1 hour slots
  enableTimeSlots: false,
  createdAt: new Date(),
  updatedAt: new Date()
});

// Date utility functions to handle timezone issues
export const createLocalDate = (year: number, month: number, day: number): Date => {
  return new Date(year, month, day, 0, 0, 0, 0);
};

export const createDateFromString = (dateString: string): Date => {
  // Parse date string in YYYY-MM-DD format and create local date
  const [year, month, day] = dateString.split('-').map(Number);
  return createLocalDate(year, month - 1, day); // month is 0-indexed
};

export const getDateKey = (date: Date): string => {
  // Create a consistent date key for comparisons
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const datesEqual = (date1: Date, date2: Date): boolean => {
  return getDateKey(date1) === getDateKey(date2);
};

export const applyBookingCountsToAvailability = (
  availabilityDates: AvailabilityDate[],
  bookingCounts?: Map<string, number> | null
): AvailabilityDate[] => {
  if (!bookingCounts || typeof bookingCounts.has !== 'function') {
    return availabilityDates;
  }

  const merged = availabilityDates.map((availability) => {
    const key = getDateKey(availability.date);
    if (!bookingCounts.has(key)) {
      return availability;
    }

    return {
      ...availability,
      currentBookings: bookingCounts.get(key) ?? 0
    };
  });

  const existingKeys = new Set(merged.map((availability) => getDateKey(availability.date)));
  bookingCounts.forEach((count, key) => {
    if (count > 0 && !existingKeys.has(key)) {
      merged.push({
        ...createEmptyAvailabilityDate(createDateFromString(key)),
        currentBookings: count
      });
    }
  });

  return merged;
};

// ============================================================================
// DELIVERY PRICING TYPES - Delivery Tier Management
// ============================================================================

// Dublin town/location data from routing table
export interface DublinLocation {
  routingKey: string;
  town: string;
  county: string;
}

// Delivery pricing tier
export interface DeliveryTier {
  id: string;
  name: string;
  price: number; // Price in euros
  locations: DublinLocation[]; // Towns assigned to this tier
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Delivery pricing settings
export interface DeliveryPricingSettings {
  id: string;
  defaultDeliveryPrice: number; // Default price for unassigned locations
  freeDeliveryThreshold: number; // Minimum order amount for free delivery
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// VAT settings for tax management
export interface VatSettings {
  id: string;
  isEnabled: boolean; // Whether VAT is enabled
  percentage: number; // VAT percentage (e.g., 23 for 23%)
  isInclusive: boolean; // true = prices include VAT, false = VAT added on top
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Image on cake pricing settings
export interface ImageOnCakeSettings {
  id: string;
  price: number; // Additional price for image on cake in euros
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Base-colour pricing: global default, then collection, then product.
// A missing key inherits the parent. An explicit 0 means the colour is free at that level.
export interface ColorPricingSettings {
  id: string;
  globalPrices: Record<string, number>;
  collectionPrices: Partial<Record<ProductCollectionType, Record<string, number>>>;
  productPrices: Partial<Record<ProductCollectionType, Record<string, Record<string, number>>>>;
  createdAt: Date;
  updatedAt: Date;
}

// Delivery pricing management operations
export interface DeliveryPricingOperations {
  createDeliveryTier: (tier: Partial<DeliveryTier>) => Promise<DeliveryTier>;
  updateDeliveryTier: (tierId: string, updates: Partial<DeliveryTier>) => Promise<void>;
  deleteDeliveryTier: (tierId: string) => Promise<void>;
  getDeliveryTiers: () => Promise<DeliveryTier[]>;
  getDeliveryTier: (tierId: string) => Promise<DeliveryTier | null>;
  getDeliveryPricingSettings: () => Promise<DeliveryPricingSettings | null>;
  updateDeliveryPricingSettings: (settings: Partial<DeliveryPricingSettings>) => Promise<void>;
  calculateDeliveryPrice: (location: string, orderTotal: number) => Promise<number>;
}

// VAT management operations
export interface VatOperations {
  getVatSettings: () => Promise<VatSettings | null>;
  updateVatSettings: (settings: Partial<VatSettings>) => Promise<void>;
  createVatSettings: (settings: Partial<VatSettings>) => Promise<VatSettings>;
}

// Image on cake pricing management operations
export interface ImageOnCakeOperations {
  getImageOnCakeSettings: () => Promise<ImageOnCakeSettings | null>;
  updateImageOnCakeSettings: (settings: Partial<ImageOnCakeSettings>) => Promise<void>;
  createImageOnCakeSettings: (settings: Partial<ImageOnCakeSettings>) => Promise<ImageOnCakeSettings>;
}

// Delivery pricing component props
export interface DeliveryPricingManagementProps {
  tiers: DeliveryTier[];
  settings: DeliveryPricingSettings | null;
  loading?: boolean;
  onCreateTier: (tier: Partial<DeliveryTier>) => void;
  onUpdateTier: (tierId: string, updates: Partial<DeliveryTier>) => void;
  onDeleteTier: (tierId: string) => void;
  onUpdateSettings: (settings: Partial<DeliveryPricingSettings>) => void;
}

// VAT management component props
export interface VatManagementProps {
  settings: VatSettings | null;
  loading?: boolean;
  onUpdateSettings: (settings: Partial<VatSettings>) => void;
}

// Image on cake pricing management component props
export interface ImageOnCakeManagementProps {
  settings: ImageOnCakeSettings | null;
  loading?: boolean;
  onUpdateSettings: (settings: Partial<ImageOnCakeSettings>) => void;
}

export interface DeliveryTierFormProps {
  tier?: DeliveryTier;
  onSubmit: (tier: Partial<DeliveryTier>) => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface DeliveryTierCardProps {
  tier: DeliveryTier;
  onEdit: (tier: DeliveryTier) => void;
  onDelete: (tier: DeliveryTier) => void;
  className?: string;
}

export interface LocationSelectorProps {
  selectedLocations: DublinLocation[];
  onLocationsChange: (locations: DublinLocation[]) => void;
  className?: string;
}

// Dublin locations data (from the routing table)
export const DUBLIN_LOCATIONS: DublinLocation[] = [
  { routingKey: 'K32', town: 'Balbriggan', county: 'Dublin' },
  { routingKey: 'A41', town: 'Ballyboughal', county: 'Dublin' },
  { routingKey: 'A94', town: 'Blackrock', county: 'Dublin' },
  { routingKey: 'A94', town: 'Booterstown', county: 'Dublin' },
  { routingKey: 'D01', town: 'Dublin 1', county: 'Dublin' },
  { routingKey: 'D02', town: 'Dublin 2', county: 'Dublin' },
  { routingKey: 'D03', town: 'Dublin 3', county: 'Dublin' },
  { routingKey: 'D04', town: 'Dublin 4', county: 'Dublin' },
  { routingKey: 'D05', town: 'Dublin 5', county: 'Dublin' },
  { routingKey: 'D06', town: 'Dublin 6', county: 'Dublin' },
  { routingKey: 'D6W', town: 'Dublin 6W', county: 'Dublin' },
  { routingKey: 'D07', town: 'Dublin 7', county: 'Dublin' },
  { routingKey: 'D08', town: 'Dublin 8', county: 'Dublin' },
  { routingKey: 'D09', town: 'Dublin 9', county: 'Dublin' },
  { routingKey: 'D10', town: 'Dublin 10', county: 'Dublin' },
  { routingKey: 'D11', town: 'Dublin 11', county: 'Dublin' },
  { routingKey: 'D12', town: 'Dublin 12', county: 'Dublin' },
  { routingKey: 'D13', town: 'Dublin 13', county: 'Dublin' },
  { routingKey: 'D14', town: 'Dublin 14', county: 'Dublin' },
  { routingKey: 'D15', town: 'Dublin 15', county: 'Dublin' },
  { routingKey: 'D16', town: 'Dublin 16', county: 'Dublin' },
  { routingKey: 'D17', town: 'Dublin 17', county: 'Dublin' },
  { routingKey: 'D18', town: 'Dublin 18', county: 'Dublin' },
  { routingKey: 'D20', town: 'Dublin 20', county: 'Dublin' },
  { routingKey: 'D22', town: 'Dublin 22', county: 'Dublin' },
  { routingKey: 'D24', town: 'Dublin 24', county: 'Dublin' },
  { routingKey: 'A42', town: 'Garristown', county: 'Dublin' },
  { routingKey: 'A96', town: 'Glenageary', county: 'Dublin' },
  { routingKey: 'K78', town: 'Lucan', county: 'Dublin' },
  { routingKey: 'K45', town: 'Lusk', county: 'Dublin' },
  { routingKey: 'K36', town: 'Malahide', county: 'Dublin' },
  { routingKey: 'A94', town: 'Monkstown', county: 'Dublin' },
  { routingKey: 'A41', town: 'Oldtown', county: 'Dublin' },
  { routingKey: 'K32', town: 'Rush', county: 'Dublin' },
  { routingKey: 'K34', town: 'Skerries', county: 'Dublin' },
  { routingKey: 'K67', town: 'Swords', county: 'Dublin' },
  { routingKey: 'K67', town: 'Dublin Airport', county: 'Dublin' }
];

// Utility functions for delivery pricing
export const createDeliveryTierId = (): string => uuidv4();

export const createEmptyDeliveryTier = (): Partial<DeliveryTier> => ({
  name: '',
  price: 0,
  locations: [],
  isActive: true
});

export const createDefaultDeliveryPricingSettings = (): DeliveryPricingSettings => ({
  id: 'default',
  defaultDeliveryPrice: 5.00,
  freeDeliveryThreshold: 50.00,
  isActive: true,
  createdAt: new Date(),
  updatedAt: new Date()
});

export const findLocationByTown = (town: string): DublinLocation | undefined => {
  return DUBLIN_LOCATIONS.find(location => 
    location.town.toLowerCase() === town.toLowerCase()
  );
};

export const searchLocations = (query: string): DublinLocation[] => {
  if (!query.trim()) return DUBLIN_LOCATIONS;
  
  const lowercaseQuery = query.toLowerCase();
  return DUBLIN_LOCATIONS.filter(location =>
    location.town.toLowerCase().includes(lowercaseQuery) ||
    location.routingKey.toLowerCase().includes(lowercaseQuery)
  );
};

export const getUnassignedLocations = (tiers: DeliveryTier[]): DublinLocation[] => {
  const assignedTowns = new Set(
    tiers.flatMap(tier => tier.locations.map(loc => loc.town))
  );
  
  return DUBLIN_LOCATIONS.filter(location => 
    !assignedTowns.has(location.town)
  );
};

export const calculateDeliveryPriceForLocation = (
  location: string,
  tiers: DeliveryTier[],
  settings: DeliveryPricingSettings | null,
  orderTotal: number = 0
): number => {
  // Check if order qualifies for free delivery
  if (settings && orderTotal >= settings.freeDeliveryThreshold) {
    return 0;
  }
  
  // Find the tier that contains this location
  const tier = tiers.find(t => 
    t.isActive && t.locations.some(loc => 
      loc.town.toLowerCase() === location.toLowerCase()
    )
  );
  
  if (tier) {
    return tier.price;
  }
  
  // Return default price if no tier found
  return settings?.defaultDeliveryPrice || 5.00;
};

// VAT calculation utility functions
export const calculateVatAmount = (
  subtotal: number,
  vatPercentage: number
): number => {
  return Math.round((subtotal * vatPercentage / 100) * 100) / 100;
};

export const calculatePriceWithVat = (
  price: number,
  vatPercentage: number
): number => {
  return Math.round((price * (1 + vatPercentage / 100)) * 100) / 100;
};

export const calculatePriceWithoutVat = (
  priceWithVat: number,
  vatPercentage: number
): number => {
  return Math.round((priceWithVat / (1 + vatPercentage / 100)) * 100) / 100;
};

export const formatVatPercentage = (percentage: number): string => {
  return `${percentage}%`;
};

// Image on cake pricing utility functions
export const createDefaultImageOnCakeSettings = (): ImageOnCakeSettings => ({
  id: 'default',
  price: 15.00, // Default price of €15
  isActive: true,
  createdAt: new Date(),
  updatedAt: new Date()
});

export const formatImageOnCakePrice = (price: number): string => {
  return `€${price.toFixed(2)}`;
};

// ============================================================================
// CONTACT FORM TYPES - Contact Form Management
// ============================================================================

// Contact form data interface
export interface ContactFormData {
  name: string;
  email: string;
  phone?: string | null;
  message: string;
}

// Contact form submission interface
export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  message: string;
  status: 'new' | 'read' | 'replied' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  ipAddress?: string;
  userAgent?: string;
}

// Contact form component props
export interface ContactFormProps {
  onSubmit: (formData: ContactFormData) => Promise<void>;
  loading?: boolean;
  className?: string;
}

// Contact page component props
export interface ContactPageProps {
  className?: string;
}

// ============================================================================
// STOREFRONT MANAGEMENT TYPES - Homepage Content Management
// ============================================================================

// Homepage product section data
export interface HomepageProductSection {
  id: string;
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  imageSrc: string;
  imageAlt: string;
  reverse: boolean;
  backgroundColor: 'white' | 'pink';
  order: number; // For ordering sections
  isActive: boolean;
  /** Firestore document ID of the linked collection (collections collection) */
  collectionId?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Hero section data
export interface HeroSectionData {
  id: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string;
  imageSrc: string;
  imageAlt?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Promotional banner data
export interface PromotionalBannerData {
  id: string;
  message: string;
  code: string;
  dates?: string;
  note?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Storefront management operations
export interface StorefrontOperations {
  // Homepage product sections
  getHomepageProductSections: () => Promise<HomepageProductSection[]>;
  createHomepageProductSection: (section: Partial<HomepageProductSection>) => Promise<HomepageProductSection>;
  updateHomepageProductSection: (id: string, updates: Partial<HomepageProductSection>) => Promise<void>;
  deleteHomepageProductSection: (id: string) => Promise<void>;
  reorderHomepageProductSections: (sections: HomepageProductSection[]) => Promise<void>;
  
  // Hero section
  getHeroSectionData: () => Promise<HeroSectionData | null>;
  updateHeroSectionData: (data: Partial<HeroSectionData>) => Promise<HeroSectionData>;
  
  // Promotional banner
  getPromotionalBannerData: () => Promise<PromotionalBannerData | null>;
  updatePromotionalBannerData: (data: Partial<PromotionalBannerData>) => Promise<PromotionalBannerData>;
}

// Storefront management component props
export interface StorefrontManagementProps {
  className?: string;
}

// Homepage product section form props
export interface HomepageProductSectionFormProps {
  section?: HomepageProductSection;
  onSubmit: (section: Partial<HomepageProductSection>) => void;
  onCancel: () => void;
  loading?: boolean;
}

// Hero section form props
export interface HeroSectionFormProps {
  data?: HeroSectionData;
  onSubmit: (data: Partial<HeroSectionData>) => void;
  onCancel: () => void;
  loading?: boolean;
}

// Promotional banner form props
export interface PromotionalBannerFormProps {
  data?: PromotionalBannerData;
  onSubmit: (data: Partial<PromotionalBannerData>) => void;
  onCancel: () => void;
  loading?: boolean;
}

// Storefront management page props
export interface StorefrontPageProps {
  className?: string;
}

// Utility functions for storefront management
export const createHomepageProductSectionId = (): string => uuidv4();

export const createEmptyHomepageProductSection = (): Partial<HomepageProductSection> => ({
  title: '',
  description: '',
  buttonText: 'Shop Now',
  buttonLink: '',
  imageSrc: '',
  imageAlt: '',
  reverse: false,
  backgroundColor: 'white',
  order: 0,
  isActive: true,
  collectionId: ''
});

export const createEmptyHeroSectionData = (): Partial<HeroSectionData> => ({
  title: '',
  subtitle: '',
  buttonText: '',
  buttonLink: '',
  imageSrc: '',
  imageAlt: '',
  isActive: true
});

export const createEmptyPromotionalBannerData = (): Partial<PromotionalBannerData> => ({
  message: '',
  code: '',
  dates: '',
  note: '',
  isActive: true
});

// ============================================================================
// COUPON TYPES - Coupon Management System
// ============================================================================

// Coupon discount types
export type CouponDiscountType = 'percentage' | 'fixed';

// Coupon status
export type CouponStatus = 'active' | 'inactive' | 'expired' | 'used_up';

// Coupon interface
export interface Coupon {
  id: string;
  code: string;
  name: string;
  description?: string;
  discountType: CouponDiscountType;
  discountValue: number; // Percentage (0-100) or fixed amount in cents
  minimumOrderAmount?: number; // Minimum order amount in cents
  maximumDiscountAmount?: number; // Maximum discount amount in cents (for percentage coupons)
  usageLimit?: number; // Maximum number of times this coupon can be used
  usedCount: number; // Number of times this coupon has been used
  isActive: boolean;
  validFrom: Date;
  validUntil?: Date;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // Admin user ID who created the coupon
}

// Coupon creation data
export interface CouponCreateData {
  code: string;
  name: string;
  description?: string;
  discountType: CouponDiscountType;
  discountValue: number;
  minimumOrderAmount?: number;
  maximumDiscountAmount?: number;
  usageLimit?: number;
  validFrom: Date;
  validUntil?: Date;
}

// Coupon update data
export interface CouponUpdateData {
  name?: string;
  description?: string;
  discountType?: CouponDiscountType;
  discountValue?: number;
  minimumOrderAmount?: number;
  maximumDiscountAmount?: number;
  usageLimit?: number;
  isActive?: boolean;
  validFrom?: Date;
  validUntil?: Date;
}

// Coupon validation result
export interface CouponValidationResult {
  isValid: boolean;
  coupon?: Coupon;
  error?: string;
  discountAmount?: number;
}

// Coupon settings (global coupon system settings)
export interface CouponSettings {
  id: string;
  isEnabled: boolean; // Whether coupon system is enabled
  allowMultipleCoupons: boolean; // Whether multiple coupons can be applied
  maxCouponsPerOrder: number; // Maximum number of coupons per order
  createdAt: Date;
  updatedAt: Date;
}

// Coupon usage tracking
export interface CouponUsage {
  id: string;
  couponId: string;
  orderId: string;
  customerEmail: string;
  discountAmount: number; // Amount discounted in cents
  usedAt: Date;
}

// Coupon management operations
export interface CouponOperations {
  createCoupon: (coupon: CouponCreateData) => Promise<Coupon>;
  updateCoupon: (couponId: string, updates: CouponUpdateData) => Promise<Coupon>;
  deleteCoupon: (couponId: string) => Promise<void>;
  getCoupon: (couponId: string) => Promise<Coupon | null>;
  getCoupons: (filters?: CouponFilters) => Promise<Coupon[]>;
  validateCoupon: (code: string, orderTotal: number) => Promise<CouponValidationResult>;
  applyCoupon: (couponId: string, orderId: string, customerEmail: string) => Promise<CouponUsage>;
  getCouponUsage: (couponId: string) => Promise<CouponUsage[]>;
  getCouponSettings: () => Promise<CouponSettings | null>;
  updateCouponSettings: (settings: Partial<CouponSettings>) => Promise<CouponSettings>;
}

// Coupon filters for searching
export interface CouponFilters {
  status?: CouponStatus[];
  isActive?: boolean;
  search?: string; // Search in code, name, description
  createdBy?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

// Coupon statistics
export interface CouponStats {
  totalCoupons: number;
  activeCoupons: number;
  expiredCoupons: number;
  totalUsage: number;
  totalDiscountGiven: number; // Total amount discounted in cents
  mostUsedCoupon?: {
    coupon: Coupon;
    usageCount: number;
  };
  recentUsage: CouponUsage[];
}

// Coupon component props
export interface CouponFieldProps {
  value: string;
  onChange: (value: string) => void;
  onApply: (code: string) => void;
  onRemove: () => void;
  loading?: boolean;
  error?: string;
  appliedCoupon?: Coupon;
  className?: string;
}

export interface CouponToggleProps {
  isEnabled: boolean;
  onToggle: (enabled: boolean) => void;
  loading?: boolean;
  className?: string;
}

export interface CouponFormProps {
  coupon?: Coupon;
  onSubmit: (coupon: CouponCreateData | CouponUpdateData) => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface CouponListProps {
  coupons: Coupon[];
  onEdit: (coupon: Coupon) => void;
  onDelete: (coupon: Coupon) => void;
  onToggleStatus: (coupon: Coupon) => void;
  loading?: boolean;
}

export interface CouponManagerProps {
  coupons: Coupon[];
  settings: CouponSettings | null;
  loading?: boolean;
  onCreateCoupon: (coupon: CouponCreateData) => void;
  onUpdateCoupon: (couponId: string, updates: CouponUpdateData) => void;
  onDeleteCoupon: (couponId: string) => void;
  onToggleCouponStatus: (couponId: string) => void;
  onUpdateSettings: (settings: Partial<CouponSettings>) => void;
}

// Utility functions for coupons
export const createCouponId = (): string => uuidv4();

export const createEmptyCoupon = (): Partial<Coupon> => ({
  code: '',
  name: '',
  description: '',
  discountType: 'percentage',
  discountValue: 0,
  minimumOrderAmount: 0,
  maximumDiscountAmount: undefined,
  usageLimit: undefined,
  usedCount: 0,
  isActive: true,
  validFrom: new Date(),
  validUntil: undefined
});

export const createDefaultCouponSettings = (): CouponSettings => ({
  id: 'default',
  isEnabled: true,
  allowMultipleCoupons: false,
  maxCouponsPerOrder: 1,
  createdAt: new Date(),
  updatedAt: new Date()
});

export const formatCouponCode = (code: string): string => {
  return code.toUpperCase().trim();
};

export const calculateCouponDiscount = (
  coupon: Coupon,
  orderTotal: number
): number => {
  if (!coupon.isActive) return 0;
  
  // Check if coupon is within valid date range
  const now = new Date();
  if (coupon.validFrom > now) return 0;
  if (coupon.validUntil && coupon.validUntil < now) return 0;
  
  // Check minimum order amount
  if (coupon.minimumOrderAmount && orderTotal < coupon.minimumOrderAmount) {
    return 0;
  }
  
  // Check usage limit
  if (coupon.usageLimit && coupon.usedCount >= coupon.usageLimit) {
    return 0;
  }
  
  let discountAmount = 0;
  
  if (coupon.discountType === 'percentage') {
    discountAmount = Math.round((orderTotal * coupon.discountValue) / 100);
    
    // Apply maximum discount limit if set
    if (coupon.maximumDiscountAmount && discountAmount > coupon.maximumDiscountAmount) {
      discountAmount = coupon.maximumDiscountAmount;
    }
  } else {
    // Fixed amount discount
    discountAmount = coupon.discountValue;
  }
  
  // Ensure discount doesn't exceed order total
  return Math.min(discountAmount, orderTotal);
};

export const isCouponValid = (coupon: Coupon, orderTotal: number): boolean => {
  if (!coupon.isActive) return false;
  
  const now = new Date();
  if (coupon.validFrom > now) return false;
  if (coupon.validUntil && coupon.validUntil < now) return false;
  
  if (coupon.minimumOrderAmount && orderTotal < coupon.minimumOrderAmount) {
    return false;
  }
  
  if (coupon.usageLimit && coupon.usedCount >= coupon.usageLimit) {
    return false;
  }
  
  return true;
};

export const getCouponStatus = (coupon: Coupon): CouponStatus => {
  if (!coupon.isActive) return 'inactive';
  
  const now = new Date();
  if (coupon.validUntil && coupon.validUntil < now) return 'expired';
  
  if (coupon.usageLimit && coupon.usedCount >= coupon.usageLimit) return 'used_up';
  
  return 'active';
};
